import { useCallback, useEffect, useRef, useState } from "react";
import { Step } from "react-joyride";

type HomeTourStep = Step & { id: string };
type Session = { accountId: number; steps: HomeTourStep[]; stepIndex: number };

export const homeTourStorageKey = (accountId: number) => `propally:home_tour_seen:v1:${accountId}`;

const getRenderedSteps = (isMobile: boolean): HomeTourStep[] => {
  const messageTarget = isMobile
    ? Array.from(document.querySelectorAll("[aria-label=\"メインナビゲーション\"] button")).find(element => element.textContent?.includes("メッセージ"))
    : document.querySelector("nav a[href=\"/offer\"]");
  const candidates = [
    // SP のショートカット行は廃止したため、案内の対象は PC の上部メニューだけ
    { id: "navigation", target: isMobile ? null : document.querySelector("nav"), content: "よく使うものは、ここに並んでいます。", placement: "bottom-start" },
    { id: "cashflow", target: document.getElementById("home-tour-cashflow"), content: "物件を登録すると、\n今月の収支がここに出ます。", placement: isMobile ? "top-start" : "bottom-start" },
    { id: "message", target: messageTarget, content: "プランナーからの提案は、\nここに届きます。", placement: isMobile ? "top" : "bottom-start" },
  ];
  return candidates.filter(step => step.target instanceof HTMLElement) as HomeTourStep[];
};

export const useHomeTour = ({ accountId, ready, isMobile }: { accountId?: number; ready: boolean; isMobile: boolean }) => {
  const [session, setSession] = useState<Session | null>(null);
  const completedAccounts = useRef(new Set<number>());

  useEffect(() => {
    if (!accountId) { setSession(null); return; }
    if (!ready) return;
    let seen = completedAccounts.current.has(accountId);
    try { seen = seen || window.localStorage.getItem(homeTourStorageKey(accountId)) === "true"; } catch (_) { /* 保存できない場合も、その画面では完了済みを保持する。 */ }
    if (seen) { setSession(null); return; }
    const steps = getRenderedSteps(isMobile);
    if (!steps.length) return;
    setSession(previous => {
      const currentId = previous?.accountId === accountId ? previous.steps[previous.stepIndex]?.id : undefined;
      const index = steps.findIndex(step => step.id === currentId);
      return { accountId, steps, stepIndex: index >= 0 ? index : 0 };
    });
  }, [accountId, ready, isMobile]);

  const complete = useCallback(() => {
    if (!session || session.accountId !== accountId) return;
    completedAccounts.current.add(session.accountId);
    try { window.localStorage.setItem(homeTourStorageKey(session.accountId), "true"); } catch (_) { /* noop */ }
    setSession(null);
  }, [accountId, session]);
  const next = useCallback(() => {
    if (!session) return;
    if (session.stepIndex === session.steps.length - 1) { complete(); return; }
    setSession(previous => previous && previous.stepIndex === session.stepIndex ? { ...previous, stepIndex: previous.stepIndex + 1 } : previous);
  }, [complete, session]);

  return {
    run: ready && !!session && session.accountId === accountId,
    steps: session && session.accountId === accountId ? session.steps : [],
    stepIndex: session?.stepIndex ?? 0,
    next,
    complete,
  };
};
